import React, { useMemo, Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { CabinetMesh } from "./CabinetVisualizer3D";

function JunctionLine({ x, height }) {
  const points = useMemo(() => {
    const arr = [];
    const segments = 20;
    for (let i = 0; i <= segments; i++) {
      arr.push(x, (i / segments) * height, 0);
    }
    return new Float32Array(arr);
  }, [x, height]);
  return (
    <lineSegments>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[points, 3]} />
      </bufferGeometry>
      <lineBasicMaterial color="#0465C1" linewidth={2} />
    </lineSegments>
  );
}

export default function AssemblyVisualizer3D({ mainConfig, moduleBConfig, assembly, catalog }) {
  const swapped = assembly?.module_order === "b_left";
  const leftConfig = swapped ? moduleBConfig : mainConfig;
  const rightConfig = swapped ? mainConfig : moduleBConfig;

  const leftSectionWidths = useMemo(() => {
    const secs = leftConfig.sections || [];
    const explicit = secs.map((s) => s.width_cm || 0);
    if (explicit.every((w) => w > 0)) return explicit;
    return Array(Math.max(1, secs.length)).fill(leftConfig.total_width / Math.max(1, secs.length));
  }, [leftConfig]);

  const rightSectionWidths = useMemo(() => {
    const secs = rightConfig.sections || [];
    const explicit = secs.map((s) => s.width_cm || 0);
    if (explicit.every((w) => w > 0)) return explicit;
    return Array(Math.max(1, secs.length)).fill(rightConfig.total_width / Math.max(1, secs.length));
  }, [rightConfig]);

  const leftW = leftConfig.total_width * 0.01;
  const rightW = rightConfig.total_width * 0.01;
  const totalW = leftW + rightW;
  const maxH = Math.max(leftConfig.height, rightConfig.height) * 0.01;

  const leftCarcassFinish = catalog?.finishes?.find((f) => f.id === leftConfig.carcass_finish_id);
  const leftDoorsFinish = catalog?.finishes?.find((f) => f.id === (leftConfig.doors_finish_id || leftConfig.carcass_finish_id));
  const rightCarcassFinish = catalog?.finishes?.find((f) => f.id === rightConfig.carcass_finish_id);
  const rightDoorsFinish = catalog?.finishes?.find((f) => f.id === (rightConfig.doors_finish_id || rightConfig.carcass_finish_id));

  const cameraTarget = useMemo(() => [0, maxH / 2 + 0.1, 0], [maxH]);
  const cameraPos = useMemo(() => {
    const maxDim = Math.max(totalW, maxH, Math.max(leftConfig.depth, rightConfig.depth) * 0.01);
    const dist = Math.max(1.6, maxDim * 2.2);
    return [dist * 0.55, dist * 0.55 + maxH / 3, dist];
  }, [totalW, maxH, leftConfig.depth, rightConfig.depth]);

  const leftX = -totalW / 2 + leftW / 2;
  const rightX = totalW / 2 - rightW / 2;
  const junctionX = -totalW / 2 + leftW;

  return (
    <div data-testid="assembly-3d-view" className="w-full aspect-[16/10] rounded-lg overflow-hidden border border-border bg-[#F5F3EF]">
      <Canvas shadows camera={{ position: cameraPos, fov: 38, near: 0.05, far: 40 }} dpr={[1, 1.5]}>
        <ambientLight intensity={0.45} />
        <directionalLight position={[4, 6, 4]} intensity={1.05} castShadow shadow-mapSize-width={1024} shadow-mapSize-height={1024} shadow-camera-left={-3} shadow-camera-right={3} shadow-camera-top={3} shadow-camera-bottom={-3} />
        <directionalLight position={[-4, 3, -3]} intensity={0.35} />
        <Suspense fallback={null}>
          {/* Module gauche */}
          <group position={[leftX, 0, 0]}>
            <CabinetMesh
              totalWidth={leftConfig.total_width}
              height={leftConfig.height}
              depth={leftConfig.depth}
              sections={leftConfig.sections || []}
              sectionWidths={leftSectionWidths}
              carcassColor={leftCarcassFinish?.swatch || "#B8935A"}
              doorsColor={leftDoorsFinish?.swatch || null}
              doorsDifferent={!!leftConfig.doors_finish_id && leftConfig.doors_finish_id !== leftConfig.carcass_finish_id}
              showDoors={true}
              templateVariant={leftConfig.template_variant || "battant"}
              options={leftConfig.options || {}}
              doorsFinishId={leftConfig.doors_finish_id}
              printMode={false}
            />
          </group>
          {/* Module droit */}
          <group position={[rightX, 0, 0]}>
            <CabinetMesh
              totalWidth={rightConfig.total_width}
              height={rightConfig.height}
              depth={rightConfig.depth}
              sections={rightConfig.sections || []}
              sectionWidths={rightSectionWidths}
              carcassColor={rightCarcassFinish?.swatch || "#B8935A"}
              doorsColor={rightDoorsFinish?.swatch || null}
              doorsDifferent={!!rightConfig.doors_finish_id && rightConfig.doors_finish_id !== rightConfig.carcass_finish_id}
              showDoors={true}
              templateVariant={rightConfig.template_variant || "battant"}
              options={rightConfig.options || {}}
              doorsFinishId={rightConfig.doors_finish_id}
              printMode={false}
            />
          </group>
          {/* Jonction chantier (ligne bleue entre les deux) */}
          <JunctionLine x={junctionX} height={maxH} />
          {/* Sol */}
          <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
            <planeGeometry args={[totalW + 1, totalW + 1]} />
            <meshStandardMaterial color="#EAEAEA" roughness={0.85} />
          </mesh>
        </Suspense>
        <OrbitControls target={cameraTarget} enablePan={false} minDistance={0.6} maxDistance={10} minPolarAngle={0.05} maxPolarAngle={Math.PI - 0.05} />
      </Canvas>
    </div>
  );
}
